import { useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    IconButton,
    InputAdornment,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { FaLink, FaMagnifyingGlass, FaPlus, FaRegImage } from "react-icons/fa6";
import TableLoading from "../TableLoading";
import { copyToClipBoard, formatCurrency, formatDateBR, formatDateShort } from "../../utils/functions";
import { getCustomerRegisterLink } from "../../helpers/http_customers_requests";
import globalToast from "./globalToast";
import CustomFilterBar from "./CustomFilterBar";
import RowActionsMenu, { collectActions } from "./RowActionsMenu";
import { gold, line, muted, tableBodyCellSx, tableCardSx, tableHeadCellSx, tableRowSx } from "./tableStyles";

const DATE_FIELDS = /(_at$|_date$|^date_|created|updated|register_date|ordered_at|delivery_date)/i;
const DATETIME_FIELDS = /(_at$|created|updated|register_date|date_time)/i;
const MONEY_FIELDS = /^(price|total|value|amount|paid|balance)$/i;

function formatDefaultValue(field, value) {
    if (value === undefined || value === null || value === "") return "-";
    if (typeof value === "string" && value.includes("R$")) return value;
    if (MONEY_FIELDS.test(field)) return formatCurrency(value);
    if (DATE_FIELDS.test(field)) {
        return DATETIME_FIELDS.test(field) && String(value).includes(":")
            ? formatDateBR(value)
            : formatDateShort(value);
    }
    return value;
}

function cellAlign(field) {
    if (field === "actions") return "right";
    if (field === "id" || MONEY_FIELDS.test(field)) return "right";
    return "left";
}

function statusSortRank(row) {
    const step = Number(row.current_step ?? row.step ?? 0);
    const delivered = Number(row.is_delivered) === 1 || step >= 9;
    if (row.is_overdue) return 0;
    if (!delivered && step < 8) return 1;
    if (!delivered && step === 8) return 2;
    return 3;
}

function compareRows(a, b, field) {
    if (field === "current_step" || field === "step") {
        const rankDiff = statusSortRank(a) - statusSortRank(b);
        if (rankDiff !== 0) return rankDiff;
        return Number(a.current_step ?? a.step ?? 0) - Number(b.current_step ?? b.step ?? 0);
    }

    const left = a[field];
    const right = b[field];
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;
    if (typeof left === "number" && typeof right === "number") return left - right;
    return String(left).localeCompare(String(right), "pt-BR", { numeric: true, sensitivity: "base" });
}

function isInteractiveTarget(target) {
    return Boolean(target?.closest?.("a, button, [role='menuitem'], input, textarea, select, [role='button']"));
}

function resolveRowHref(row, getRowHref, actions) {
    if (typeof getRowHref === "function") {
        return getRowHref(row) || null;
    }
    if (typeof actions !== "function") return null;

    const items = collectActions(actions(row)).filter((item) => item.to && !item.disabled);

    return items[0]?.to ?? null;
}

export function CustomDataTable({
    title = "",
    canAddData = false,
    addPath,
    columns,
    data = [],
    actions,
    disabledAddButton,
    weights,
    steps,
    currentStep,
    answeredFunction,
    paymentInfo,
    isLoading,
    createWithLink,
    filters = [],
    filterValues = {},
    onFilterChange = () => {},
    showSearch = true,
    getRowHref,
}) {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sort, setSort] = useState({ field: null, direction: "asc" });
    const [registerLinkLoading, setRegisterLinkLoading] = useState(false);

    const handleCopyRegisterLink = async () => {
        if (registerLinkLoading) return;
        setRegisterLinkLoading(true);
        try {
            const data = await getCustomerRegisterLink();
            if (data?.link) {
                await copyToClipBoard(data.link, "Link de cadastro");
            } else {
                globalToast.error("Não foi possível gerar o link de cadastro.");
            }
        } catch (error) {
            globalToast.error(
                error?.response?.data?.message || "Não foi possível gerar o link de cadastro."
            );
        } finally {
            setRegisterLinkLoading(false);
        }
    };
    const rowsPerPage = 10;

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchTerm(searchInput);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const filteredData = useMemo(() => {
        const rows = Array.isArray(data) ? [...data] : [];
        const queried = !searchTerm
            ? rows
            : rows.filter((row) => {
                const query = searchTerm.toLowerCase();
                return columns.some((column) => String(row[column.field] ?? "").toLowerCase().includes(query));
            });

        if (!sort.field) return queried;

        return queried.sort((a, b) => {
            const result = compareRows(a, b, sort.field);
            return sort.direction === "desc" ? -result : result;
        });
    }, [data, searchTerm, columns, sort]);

    const pageCount = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
    const safePage = Math.min(currentPage, pageCount);
    const paginatedData = filteredData.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);
    const startItem = filteredData.length === 0 ? 0 : (safePage - 1) * rowsPerPage + 1;
    const endItem = Math.min(safePage * rowsPerPage, filteredData.length);
    const renderCellContent = (column, row) => {
        switch (column.field) {
            case "image":
                return (
                    <Avatar alt={row.complete_name || row.name} src={row.image} sx={{ height: 36, width: 36, bgcolor: "#F6F5F1", color: muted }}>
                        <FaRegImage size={14} />
                    </Avatar>
                );
            case "weights":
                return weights ? weights(row) : null;
            case "actions":
                return actions ? <RowActionsMenu>{actions(row)}</RowActionsMenu> : null;
            case "step":
                return steps ? steps(row) : null;
            case "current_step":
                return currentStep ? currentStep(row) : null;
            case "is_answered":
                return answeredFunction ? answeredFunction(row) : null;
            case "payment_info":
                return paymentInfo ? paymentInfo(row) : null;
            default:
                return formatDefaultValue(column.field, row[column.field]);
        }
    };

    if (isLoading) {
        return <TableLoading />;
    }

    return (
        <Card elevation={0} sx={tableCardSx}>
            <CustomFilterBar
                filters={filters}
                values={filterValues}
                onChange={(field, value) => {
                    onFilterChange(field, value);
                    setCurrentPage(1);
                }}
                search={
                    showSearch ? (
                        <TextField
                            size="small"
                            placeholder="Pesquisar"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 }, maxWidth: { xs: "100%", sm: 420 }, bgcolor: "#fff" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FaMagnifyingGlass color={muted} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    ) : null
                }
                actions={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}>
                        {createWithLink ? (
                            <Tooltip title="Copiar link de cadastro">
                                <span>
                                    <IconButton
                                        onClick={handleCopyRegisterLink}
                                        disabled={registerLinkLoading}
                                        sx={{ color: gold }}
                                    >
                                        <FaLink />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : null}
                        {!disabledAddButton && canAddData ? (
                            <Button
                                color="secondary"
                                variant="contained"
                                component={Link}
                                to={addPath}
                                startIcon={<FaPlus />}
                                sx={styles.addButton}
                            >
                                {title ? `Adicionar ${title}` : "Adicionar"}
                            </Button>
                        ) : null}
                    </Box>
                }
            />

            <Box sx={{ overflow: "auto" }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    align={cellAlign(column.field)}
                                    sx={tableHeadCellSx}
                                    sortDirection={sort.field === column.field ? sort.direction : false}
                                >
                                    {column.sortable ? (
                                        <TableSortLabel
                                            active={sort.field === column.field}
                                            direction={sort.field === column.field ? sort.direction : "asc"}
                                            onClick={() => {
                                                setCurrentPage(1);
                                                setSort((prev) => {
                                                    if (prev.field !== column.field) {
                                                        return { field: column.field, direction: "asc" };
                                                    }
                                                    if (prev.direction === "asc") {
                                                        return { field: column.field, direction: "desc" };
                                                    }
                                                    return { field: null, direction: "asc" };
                                                });
                                            }}
                                        >
                                            {column.headerName}
                                        </TableSortLabel>
                                    ) : (
                                        column.headerName
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ ...tableBodyCellSx, py: 6, textAlign: "center" }}>
                                    <Typography sx={{ color: muted }}>Nenhum registro encontrado.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((row, index) => {
                                const href = resolveRowHref(row, getRowHref, actions);
                                const openRow = (event) => {
                                    if (!href || event.defaultPrevented || isInteractiveTarget(event.target)) return;
                                    if (event.metaKey || event.ctrlKey || event.button === 1) {
                                        window.open(href, "_blank", "noopener,noreferrer");
                                        return;
                                    }
                                    navigate(href);
                                };

                                return (
                                    <TableRow
                                        key={row.id ?? index}
                                        hover
                                        onClick={openRow}
                                        onAuxClick={openRow}
                                        onKeyDown={(event) => {
                                            if (!href) return;
                                            if (event.key !== "Enter" && event.key !== " ") return;
                                            if (isInteractiveTarget(event.target)) return;
                                            event.preventDefault();
                                            navigate(href);
                                        }}
                                        tabIndex={href ? 0 : undefined}
                                        sx={{
                                            ...tableRowSx,
                                            ...(href ? { cursor: "pointer", "&:focus-visible": { outline: `2px solid ${gold}`, outlineOffset: -2 } } : {}),
                                        }}
                                    >
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.field}
                                                align={cellAlign(column.field)}
                                                sx={tableBodyCellSx}
                                                onClick={column.field === "actions" ? (event) => event.stopPropagation() : undefined}
                                            >
                                                {renderCellContent(column, row)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Box>

            <Box sx={styles.footer}>
                <Typography sx={{ color: muted, fontSize: 13, textAlign: "left" }}>
                    {filteredData.length === 0
                        ? "0 registros"
                        : `${startItem} a ${endItem} de ${filteredData.length} registro${filteredData.length === 1 ? "" : "s"}`}
                </Typography>
                {pageCount > 1 ? (
                    <Pagination
                        count={pageCount}
                        page={safePage}
                        onChange={(_event, page) => setCurrentPage(page)}
                        color="secondary"
                        shape="rounded"
                        size="small"
                    />
                ) : null}
            </Box>
        </Card>
    );
}

const styles = {
    addButton: {
        boxShadow: "none",
        minHeight: 40,
        whiteSpace: "nowrap",
        "&:hover": { boxShadow: "none" },
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${line}`,
        bgcolor: "#fff",
    },
};
